import { useState } from 'react';
import { useGameStore, useJwt } from '@campus/core';
import { AdminMembers } from './AdminMembers.js';
import { AdminStats } from './AdminStats.js';
import { AdminRecordings } from './AdminRecordings.js';
import { AdminDesks } from './AdminDesks.js';
import { AdminFeedback } from './AdminFeedback.js';
import { RECORDING_ENABLED } from '../../lib/features.js';
import { EditorPage } from './EditorPage.js';
import { Icon, Button, LogoMark, cx, type IconName } from '../ds/index.js';
import { AdminCallPill } from './AdminCallPill.js';

function decodeJwt(jwt: string | null): { role: string; isAdmin: boolean } | null {
  if (!jwt) return null;
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    const role = (payload.role ?? 'member') as string;
    return { role, isAdmin: ['admin', 'owner'].includes(role.toLowerCase()) };
  } catch { return null; }
}

type Page = 'members' | 'stats' | 'map' | 'recordings' | 'desks' | 'feedback';

const NAV: Array<{ key: Page; label: string; icon: IconName }> = [
  { key: 'members', label: 'Membres', icon: 'users' },
  { key: 'stats', label: 'Statistiques', icon: 'bar-chart' },
  { key: 'map', label: 'Carte', icon: 'map' },
  { key: 'desks', label: 'Bureaux', icon: 'monitor' },
  { key: 'recordings', label: 'Enregistrements', icon: 'video' },
  { key: 'feedback', label: 'Retours', icon: 'flag' },
];

/**
 * AdminLayout — Interface d'administration classique (sans Phaser).
 * Menu latéral + contenu. Ordinateur uniquement.
 */
export function AdminLayout() {
  const jwt = useJwt();
  const adminPage = useGameStore((s) => s.adminPage);
  const setAdminPage = useGameStore((s) => s.setAdminPage);
  const displayName = useGameStore((s) => s.displayName);
  const jwtInfo = decodeJwt(jwt);
  // Menu repliable (icônes seules), mémorisé le temps de la session d'admin.
  const [collapsed, setCollapsed] = useState(false);

  // Guard: non authentifie ou pas admin → retour au campus
  if (!jwt || !jwtInfo?.isAdmin) {
    setAdminPage(null);
    return null;
  }

  const isOwner = jwtInfo.role.toUpperCase() === 'OWNER';
  // Retours : OWNER seulement (même règle que l'API et la RLS).
  const navItems = NAV.filter((item) => (item.key !== 'recordings' || RECORDING_ENABLED) && (item.key !== 'feedback' || isOwner));

  // Éditeur de carte : plein écran, sa barre a son propre bouton de retour.
  if (adminPage === 'map') {
    return (
      <div className="pl-admin">
        <main style={{ flex: 1, overflow: 'hidden' }}><EditorPage /></main>
        <AdminCallPill />
      </div>
    );
  }

  return (
    <div className="pl-admin">
      <aside aria-label="Administration" className={cx('pl-admin-side', collapsed && 'pl-admin-side-collapsed')}>
        <header className="pl-admin-head">
          {!collapsed && <LogoMark size={32} />}
          {!collapsed && (
            <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ font: '700 18px/1.25 var(--font-display)' }}>Administration</span>
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName ?? 'Admin'}</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            icon={collapsed ? 'chevrons-right' : 'chevrons-left'}
            aria-label={collapsed ? 'Déplier le menu' : 'Replier le menu'}
            onClick={() => setCollapsed(!collapsed)}
          />
        </header>

        <nav className="pl-admin-nav">
          {navItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={cx('pl-nav-item', adminPage === item.key && 'pl-nav-on')}
              aria-current={adminPage === item.key ? 'page' : undefined}
              title={collapsed ? item.label : undefined}
              onClick={() => setAdminPage(item.key)}
            >
              <Icon name={item.icon} size={16} />
              {!collapsed && item.label}
            </button>
          ))}
        </nav>

        <div className="pl-admin-foot">
          <button type="button" className="pl-nav-item" onClick={() => setAdminPage(null)} title={collapsed ? 'Retour au campus' : undefined}>
            <Icon name="arrow-left" size={16} />
            {!collapsed && 'Retour au campus'}
          </button>
        </div>
      </aside>

      <main className="pl-admin-main">
        {adminPage === 'members' && <AdminMembers />}
        {adminPage === 'stats' && <AdminStats />}
        {adminPage === 'recordings' && RECORDING_ENABLED && <AdminRecordings />}
        {adminPage === 'desks' && <AdminDesks />}
        {adminPage === 'feedback' && isOwner && <AdminFeedback />}
      </main>
      <AdminCallPill />
    </div>
  );
}
